import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyRound, Eye, EyeOff, CheckCircle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ResetPasswordModal({ open, onClose }: ResetPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Walidacja na żywo
  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= 8 && password === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      toast.error('Błąd: ' + error.message);
      setLoading(false);
      return;
    }

    setDone(true);
    setLoading(false);
    toast.success('Hasło zostało zmienione ✓');
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setPassword('');
      setConfirm('');
      setDone(false);
    }, 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="rp-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: 'oklch(0.05 0.01 280 / 0.85)', backdropFilter: 'blur(8px)' }}
        >
          <motion.div
            key="rp-panel"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: 'spring', stiffness: 340, damping: 38 }}
            className="relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden"
            style={{ background: 'oklch(0.12 0.026 280)', borderColor: 'oklch(0.28 0.05 280)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Glow top bar */}
            <div className="h-1 w-full" style={{
              background: 'linear-gradient(90deg, var(--primary), var(--accent))',
            }} />

            <div className="px-7 py-7">
              {done ? (
                /* ── SUKCES ── */
                <motion.div
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center gap-5 py-4 text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 280, delay: 0.1 }}
                  >
                    <CheckCircle className="w-16 h-16 text-green-400" />
                  </motion.div>
                  <h2 className="text-2xl font-black text-foreground"
                    style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    HASŁO ZMIENIONE
                  </h2>
                  <p className="text-muted-foreground text-sm"
                    style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '1rem' }}>
                    Możesz teraz zalogować się nowym hasłem.
                  </p>
                  <Button onClick={handleClose} className="w-full mt-2 font-bold"
                    style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}>
                    Zamknij
                  </Button>
                </motion.div>
              ) : (
                /* ── FORMULARZ ── */
                <>
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}>
                        <KeyRound className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-black text-foreground leading-none"
                          style={{ fontFamily: 'Orbitron, sans-serif' }}>
                          NOWE HASŁO
                        </h2>
                        <p className="text-xs text-muted-foreground mt-0.5"
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          Ustaw nowe hasło do konta
                        </p>
                      </div>
                    </div>
                    <button onClick={handleClose}
                      className="text-muted-foreground hover:text-foreground transition-colors p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Nowe hasło */}
                    <div>
                      <label className="block text-xs font-bold tracking-widest text-muted-foreground mb-2"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        NOWE HASŁO
                      </label>
                      <div className="relative">
                        <Input
                          type={showPass ? 'text' : 'password'}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder="Min. 8 znaków"
                          className="pr-10 border-border/60 bg-[oklch(0.16_0.03_280)] text-foreground placeholder:text-muted-foreground/40 focus:border-primary"
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}
                          autoFocus
                        />
                        <button type="button"
                          onClick={() => setShowPass(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                          {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {tooShort && (
                        <p className="text-xs text-destructive mt-1.5" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                          Hasło musi mieć co najmniej 8 znaków
                        </p>
                      )}
                    </div>

                    {/* Potwierdź hasło */}
                    <div>
                      <label className="block text-xs font-bold tracking-widest text-muted-foreground mb-2"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        POTWIERDŹ HASŁO
                      </label>
                      <div className="relative">
                        <Input
                          type={showConfirm ? 'text' : 'password'}
                          value={confirm}
                          onChange={e => setConfirm(e.target.value)}
                          placeholder="Powtórz nowe hasło"
                          className="pr-10 border-border/60 bg-[oklch(0.16_0.03_280)] text-foreground placeholder:text-muted-foreground/40 focus:border-primary"
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}
                        />
                        <button type="button"
                          onClick={() => setShowConfirm(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                          {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {mismatch && (
                        <p className="text-xs text-destructive mt-1.5" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                          Hasła nie są identyczne
                        </p>
                      )}
                    </div>

                    {/* Pasek siły hasła */}
                    {password.length > 0 && (
                      <PasswordStrengthBar password={password} />
                    )}

                    <Button
                      type="submit"
                      disabled={!canSubmit || loading}
                      className="w-full gap-2 font-black tracking-wider py-5 mt-2 text-sm disabled:opacity-40"
                      style={{
                        fontFamily: 'Orbitron, sans-serif',
                        background: canSubmit
                          ? 'linear-gradient(135deg, var(--primary), var(--accent))'
                          : 'oklch(0.22 0.04 280)',
                        boxShadow: canSubmit ? '0 0 20px oklch(0.65 0.32 280 / 0.3)' : 'none',
                        transition: 'all 0.2s',
                      }}
                    >
                      {loading
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> ZAPISYWANIE...</>
                        : <><KeyRound className="w-4 h-4" /> USTAW NOWE HASŁO</>
                      }
                    </Button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Pasek siły hasła
function PasswordStrengthBar({ password }: { password: string }) {
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ['Słabe', 'Przeciętne', 'Dobre', 'Silne'];
  const colors = [
    'oklch(0.62 0.28 15)',
    'oklch(0.72 0.24 45)',
    'oklch(0.65 0.26 150)',
    'oklch(0.65 0.26 150)',
  ];

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="flex-1 h-1 rounded-full transition-all duration-300"
            style={{ background: i < score ? colors[score - 1] : 'oklch(0.22 0.04 280)' }} />
        ))}
      </div>
      <p className="text-xs" style={{
        fontFamily: 'Rajdhani, sans-serif',
        color: score > 0 ? colors[score - 1] : 'var(--muted-foreground)',
      }}>
        Siła hasła: <strong>{labels[score - 1] ?? 'Zbyt krótkie'}</strong>
        {score < 4 && <span className="text-muted-foreground ml-1">
          {!checks[1] && ' · dodaj wielką literę'}
          {!checks[2] && ' · dodaj cyfrę'}
          {!checks[3] && ' · dodaj znak specjalny'}
        </span>}
      </p>
    </div>
  );
}
